import * as React from 'react'
import moment from 'moment'
import * as Tooltip from 'react-tooltip'
import { Link } from 'react-router'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { IPOSListing } from '@getgreenline/homi-shared'
import { Currency } from '../../../../../components/Currency'
import { Routes } from '../../../../../constants/Routes'
import { Track } from '../../../../../Track'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  listing: IPOSListing
  onUpdate: (params: { wooCommerceProductId?: number; wooCommerceSyncDate: string }) => void
}

interface State {
  isSyncing: boolean
  isDeleting: boolean
  errorMessage?: string
  showAdvanced: boolean
}

@inject('currentCompanyStore')
@observer
export class ProductListingRow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isSyncing: false,
      isDeleting: false,
      showAdvanced: false,
    }
  }

  syncProduct() {
    if (this.props.listing.sku) {
      this.setState({ isSyncing: true, errorMessage: undefined })
      return this.props
        .currentCompanyStore!.syncWooCommerceProduct(this.props.listing.id)
        .then((result) => {
          Track.track('dashboard:integrations_wooCommerceProductSync', {
            productId: this.props.listing.id,
          })
          this.setState({ isSyncing: false })
          this.props.onUpdate(result)
        })
        .catch((error) => {
          Track.track('dashboard:integrations_wooCommerceProductSyncError', {
            productId: this.props.listing.id,
          })
          this.setState({
            isSyncing: false,
            errorMessage: 'There was an error syncing',
          })
        })
    }
  }

  syncInventory() {
    this.setState({ isSyncing: true, errorMessage: undefined })
    return this.props
      .currentCompanyStore!.syncWooCommerceInventory(this.props.listing.id)
      .then(() => {
        Track.track('dashboard:integrations_wooCommerceInventorySync', {
          productId: this.props.listing.id,
        })
        this.setState({ isSyncing: false })
        this.props.onUpdate({ wooCommerceSyncDate: new Date().toString() })
      })
      .catch((error) => {
        Track.track('dashboard:integrations_wooCommerceInventorySyncError', {
          productId: this.props.listing.id,
        })
        this.setState({
          isSyncing: false,
          errorMessage: 'There was an error syncing',
        })
      })
  }

  delete() {
    if (window.confirm('This will delete the product on WooCommerce')) {
      this.setState({ errorMessage: undefined })
      return this.props
        .currentCompanyStore!.deleteWooCommerceProduct(this.props.listing.id)
        .then(() => {
          this.props.onUpdate({ wooCommerceSyncDate: new Date().toString() })
        })
        .catch((error) => {
          this.setState({
            isSyncing: false,
            errorMessage: 'There was an error syncing',
          })
        })
    }
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const listing = this.props.listing

    const errorButton = (
      <button
        className='btn btn-sm mr-2 btn-danger'
        onClick={() => {
          this.syncProduct()
        }}
      >
        <div data-tip data-for={`wordpress-product-id-${listing.id}`}>
          Error
        </div>
        <Tooltip id={`wordpress-product-id-${listing.id}`} place='right' effect='solid'>
          <div>Some common reasons for errors:</div>
          <br />
          <li>Only sync 1 product at a time.</li>
          <li>Wordpress can be very slow depending on your hosting provider.</li>
          <br />
          <li>Check your Wordpress settings.</li>
          <li>
            Make sure your url starts with http or https, and that it doesn't have a trailing /
          </li>
        </Tooltip>
      </button>
    )

    let productSyncButton: JSX.Element | undefined
    if (!listing.sku) {
      productSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'SKU required',
          }}
        />
      )
    } else if (this.state.isSyncing) {
      productSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'Syncing...',
          }}
        />
      )
    } else if (this.state.errorMessage) {
      productSyncButton = errorButton
    } else {
      productSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            onClick: () => this.syncProduct(),
          }}
          titleProps={{
            title: 'Sync product',
          }}
        />
      )
    }

    let variantSyncButton: JSX.Element | undefined
    if (!listing.sku) {
      variantSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'SKU required',
          }}
        />
      )
    } else if (this.state.isSyncing) {
      variantSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'Syncing...',
          }}
        />
      )
    } else if (this.state.errorMessage) {
      variantSyncButton = errorButton
    } else {
      variantSyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            type: 'default',
            size: 'default',
            onClick: () => this.syncProduct(),
          }}
          titleProps={{
            title: 'Sync variant',
          }}
        />
      )
    }

    let inventorySyncButton: JSX.Element | undefined

    if (!listing.wooCommerceSyncDate) {
      inventorySyncButton = undefined
    } else if (!listing.sku) {
      inventorySyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'SKU required',
          }}
        />
      )
    } else if (this.state.isSyncing) {
      inventorySyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'Syncing...',
          }}
        />
      )
    } else if (this.state.errorMessage) {
      inventorySyncButton = errorButton
    } else {
      inventorySyncButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            type: 'default',
            size: 'default',
            onClick: () => this.syncInventory(),
          }}
          titleProps={{
            title: 'Sync inventory',
          }}
        />
      )
    }

    let removeButton: JSX.Element | undefined
    if (!listing.wooCommerceSyncDate) {
      removeButton = undefined
    }
    if (!listing.sku) {
      removeButton = (
        <BlazeButton
          buttonProps={{
            className: 'mr-2',
            disabled: true,
          }}
          titleProps={{
            title: 'SKU required',
          }}
        />
      )
    } else {
      removeButton = (
        <BlazeButton
          buttonProps={{
            className: 'text-muted',
            type: 'default',
            size: 'default',
            disabled: this.state.isDeleting,
            onClick: () => this.delete(),
          }}
          titleProps={{
            title: 'X',
          }}
        />
      )
    }

    let syncDate: JSX.Element
    if (listing.wooCommerceSyncDate) {
      const fromNow = moment(listing.wooCommerceSyncDate).fromNow()
      syncDate = (
        <small className='text-muted' style={{ whiteSpace: 'nowrap' }}>
          {fromNow}
        </small>
      )
    } else {
      syncDate = <small />
    }

    const syncStatus = (
      <>
        <span
          className={`mr-2 fa fa-circle ${
            listing.wooCommerceSyncDate ? 'text-success' : 'text-danger'
          }`}
        />
        {syncDate}
      </>
    )

    let inventoryDisplay: JSX.Element | undefined
    if (listing.trackInventory) {
      inventoryDisplay = <span>{listing.quantity}</span>
    } else {
      inventoryDisplay = <span></span>
    }

    return (
      <tr style={listing.parentProductId ? { backgroundColor: '#EFEFEF' } : {}} key={listing.id}>
        <td className='text-muted'>
          {listing.sku ? listing.sku : <span className='text-danger'>SKU required</span>}
        </td>
        <td>
          <div>
            <Link to={Routes.EDIT_PRODUCT(company.id, listing.parentProductId || listing.id)}>
              {listing.name}
            </Link>
          </div>
          {listing.childProducts.length > 0 ? (
            <div className='text-muted'>{listing.childProducts.length} variants</div>
          ) : undefined}
        </td>
        <td>{inventoryDisplay}</td>
        <td>
          {listing.childProducts.length === 0 && listing.price ? (
            <Currency centValue={listing.price} />
          ) : undefined}
        </td>
        <td>{syncStatus}</td>
        <td>
          {listing.sku !== null && (
            <>
              {listing.parentProductId ? variantSyncButton : productSyncButton}
              {inventorySyncButton}
              {removeButton}
            </>
          )}
        </td>
      </tr>
    )
  }
}
